/**
 * The vocabulary shared by the SDF-sampled building models (types/building*.ts): the inclusion
 * threshold their evaluators test against, the yaw rotation their query points go through, and the
 * torch they hang beside a door.
 *
 * Lives beside the specs rather than in the registry so a type module can be written without
 * importing the registry that imports it.
 */
import { rasterizePartsToStampVoxels } from '../../voxel/drawing.js';
import { TORCH_PARTS } from '../../voxel/buildPresets.js';
import { mat } from '../../materials/index.js';
import { TWO_PI, rotateXZInto } from '../../util/math.js';
import { lampOffsetZ, lampPart } from './coarseModel.js';
import { MAX_LAMP_LOD } from './lodLevels.js';
export { TWO_PI };
/** SDF threshold for voxel inclusion - sqrt(2)/2 handles 45° rotations */
export const SDF_THRESHOLD = 0.71;
/**
 * Rotate a query point into a building's own (unrotated) space.
 *
 * `util/math.rotateXZInto` is the one definition of the two lines; this is the building generators'
 * convention on top of it — the query point goes through +yaw so the SHAPE reads as rotated by -yaw,
 * which is the sign every coarse path has to match (see drawing.rasterizePartsAtScale). The scratch is
 * module-level because these loops run ~95k times per instance and used to allocate per sample.
 */
const invScratch = { x: 0, z: 0 };
export function rotateInv(x, z, cos, sin) {
    rotateXZInto(invScratch, x, z, cos, sin);
    return invScratch;
}
/**
 * Torch voxels (the Torch build preset's shape) as StampVoxels, base at the origin, +Y up.
 * Rasterized once from the shared TORCH_PARTS via the shared parts→voxel rasterizer, so build
 * placement and terrain generation use the exact same definition.
 */
export const TORCH_STAMP_VOXELS = rasterizePartsToStampVoxels(TORCH_PARTS);
/**
 * The torch laid horizontal, pointing out of the building's front (local −Z) wall: the authored +Y
 * column is rotated −90° about X (`(x,y,z) → (x, z, −y)`), so the wood base sits at the wall face and
 * the lava tip juts ~4 voxels out along −Z. Building yaw is applied per-voxel in `pushTorchAt`.
 */
const TORCH_OUT_VOXELS = TORCH_STAMP_VOXELS.map((v) => ({
    x: v.x, y: v.z, z: -v.y, material: v.material, weight: v.weight,
}));
/**
 * Push an outward-pointing torch into `voxels` at building-LOCAL anchor (localX, localZ), base at
 * `baseY`. The torch points out of the front (−Z) wall; the whole shape (anchor + each voxel's XZ)
 * is rotated local→world by the building yaw (cos/sin) so it points out of the rotated front face.
 */
const pushScratch = { x: 0, z: 0 };
export function pushTorchAt(voxels, localX, localZ, baseY, cos, sin) {
    for (const v of TORCH_OUT_VOXELS) {
        // The torch's own placement rotates local -> world, so it takes the FORWARD sign (not rotateInv).
        rotateXZInto(pushScratch, localX + v.x, localZ + v.z, cos, -sin);
        voxels.push({
            x: Math.round(pushScratch.x), y: baseY + v.y, z: Math.round(pushScratch.z),
            material: v.material, weight: v.weight,
        });
    }
}
/**
 * The coarse counterpart of `pushTorchAt`: a building's exterior door torches as ONE emissive blob,
 * prepended to its stand-in's parts.
 *
 * ONE COPY, and the three buildings differed in exactly two things — how often they are lit and how
 * far the front face is out. Both are arguments now; everything else was identical three times over,
 * and every line of it is a policy that must not drift between the three: it is a LIGHT rather than a
 * torch (block light is seeded only from voxels that pack SOLID, so a faithful sub-voxel flame emits
 * nothing at all), it stands OFF the wall (`lava` is a liquid-type material, and inside the wall it
 * would take that voxel out of the solid mesh layer and leave a hole in it), it is SNAPPED to the
 * lattice from lod 4 (where an off-lattice blob catches two rows either side and draws four voxels of
 * light beside a building drawn with one), and it retires at MAX_LAMP_LOD.
 *
 * PREPENDED: the parts composite takes the material of the part with the strictly greatest weight, so
 * ties go to the earliest part and the lamp has to win the voxel it shares with the wall behind it.
 *
 * @param lit    the instance's own roll, which each type owns because the fraction of buildings lit is
 *               a fact about that building (and feeds StampSpec.coarseSeedClass).
 * @param faceZ  half-extent of the front (−Z) face the lamp stands clear of — `radius` for a round
 *               building, `halfDepth` for a rectangular one.
 */
export function pushLamp(parts, lit, doorHeight, faceZ, lod, stride) {
    if (!lit || lod > MAX_LAMP_LOD)
        return;
    parts.unshift(lampPart(doorHeight, lampOffsetZ(faceZ, stride, lod), mat('lava'), stride, lod >= 4));
}
//# sourceMappingURL=buildingCommon.js.map